<template>
  <div :class="['graph-stage', `is-${state}`]">
    <div class="graph-stage__hint">
      <div :class="['dot', { 'is-running': state === 'running' }]"></div>
      {{ hintText }}
    </div>
    <svg
      viewBox="0 0 880 360"
      preserveAspectRatio="xMidYMid meet"
      class="w-full h-full mx-auto"
    >
      <g v-for="(edge, idx) in renderEdges" :key="edge.from + edge.to">
        <path
          :id="`ai-edge-${idx}`"
          :d="edgePath(edge)"
          :class="['edge', edge.ok ? 'is-ok' : 'is-bad']"
        />
        <circle r="3" :class="['edge-particle', edge.ok ? 'is-ok' : 'is-bad']">
          <animateMotion
            :dur="`${3 + (idx % 2)}s`"
            repeatCount="indefinite"
            rotate="auto"
          >
            <mpath :href="`#ai-edge-${idx}`" />
          </animateMotion>
        </circle>
      </g>
      <g
        v-for="node in positionedNodes"
        :key="node.id"
        :class="[
          'node-group',
          state === 'done' && !node._placeholder
            ? `node-${node.status}`
            : 'node-neutral',
        ]"
        :transform="`translate(${node.x},${node.y})`"
      >
        <rect
          v-if="node.id === activeDocId && state === 'running'"
          class="node-pulse-ring"
          x="-3"
          y="-3"
          width="246"
          height="106"
          rx="16"
        />
        <rect class="node-plate" width="240" height="100" rx="14" />
        <foreignObject x="0" y="0" width="240" height="100">
          <div xmlns="http://www.w3.org/1999/xhtml" class="node-inner">
            <div v-if="node._placeholder" class="node-name-skeleton">
              <div class="skeleton-bar skeleton-bar--title"></div>
            </div>
            <div v-else class="node-name">{{ node.name }}</div>
            <ul v-if="state !== 'idle'" class="node-bullets">
              <template v-if="state === 'running' || node._placeholder">
                <li v-for="i in 2" :key="`s-${i}`" class="node-bullet-skeleton">
                  <div class="skeleton-bar"></div>
                </li>
              </template>
              <template v-else>
                <template v-if="(node.bullets || []).length">
                  <li
                    v-for="(b, i) in (node.bullets || []).slice(0, 2)"
                    :key="i"
                    :class="[
                      'node-bullet node-bullet-reveal',
                      b.ok === true
                        ? 'is-ok'
                        : b.ok === 'warn'
                        ? 'is-warn'
                        : 'is-bad',
                    ]"
                    :style="{ animationDelay: `${i * 120}ms` }"
                  >
                    <div>{{ b.text }}</div>
                  </li>
                </template>
                <li
                  v-else
                  class="node-bullet node-bullet-reveal is-ok"
                  :style="{ animationDelay: '0ms' }"
                >
                  <div>{{ $t("Замечаний нет") }}</div>
                </li>
              </template>
            </ul>
          </div>
        </foreignObject>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  edges: { type: Array, default: () => [] },
  state: {
    type: String,
    default: "done",
    validator: (v) => ["idle", "running", "done"].includes(v),
  },
  activeDocId: { type: String, default: null },
  placeholderCount: { type: Number, default: 3 },
});

// Размеры stage соответствуют viewBox SVG ниже
const STAGE_W = 880;
const STAGE_H = 360;
const NODE_W = 240;
const NODE_H = 100;

// Раскладывает count узлов сеткой (≤4 → 2 колонки, иначе 3), равномерно центрируя их
function layoutPositions(count) {
  if (count <= 0) return [];
  if (count === 1) {
    return [{ x: (STAGE_W - NODE_W) / 2, y: (STAGE_H - NODE_H) / 2 }];
  }
  const cols = count <= 4 ? 2 : 3;
  const rows = Math.ceil(count / cols);
  const gapY =
    rows > 1 ? (STAGE_H - rows * NODE_H) / (rows + 1) : (STAGE_H - NODE_H) / 2;

  const positions = [];
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const itemsInRow = Math.min(cols, count - row * cols);
    const gapX = (STAGE_W - itemsInRow * NODE_W) / (itemsInRow + 1);
    const x =
      itemsInRow === 1 ? (STAGE_W - NODE_W) / 2 : gapX + col * (NODE_W + gapX);
    const y =
      rows === 1 ? (STAGE_H - NODE_H) / 2 : gapY + row * (NODE_H + gapY);
    positions.push({ x, y });
  }
  return positions;
}

// Placeholder-узлы пока state=running и реальных nodes нет
const renderNodes = computed(() => {
  const incoming = Array.isArray(props.nodes) ? props.nodes : [];
  if (props.state === "running" && incoming.length === 0) {
    return Array.from({ length: props.placeholderCount }, (_, i) => ({
      id: `__placeholder-${i}`,
      name: "",
      status: "neutral",
      bullets: [],
      _placeholder: true,
    }));
  }
  return incoming;
});

const positionedNodes = computed(() => {
  const nodes = renderNodes.value;
  const positions = layoutPositions(nodes.length);
  return nodes.map((n, i) => ({
    ...n,
    x: positions[i]?.x ?? 0,
    y: positions[i]?.y ?? 0,
  }));
});

const renderEdges = computed(() => {
  const incoming = Array.isArray(props.edges) ? props.edges : [];
  if (props.state === "running" && (props.nodes?.length ?? 0) === 0) {
    const list = [];
    for (let i = 1; i < props.placeholderCount; i++) {
      list.push({
        from: "__placeholder-0",
        to: `__placeholder-${i}`,
        ok: true,
      });
    }
    return list;
  }
  return incoming;
});

const hintText = computed(() => {
  if (props.state === "idle") return t("Готов к запуску");
  if (props.state === "running") return t("Анализ связей · поток данных");
  return t("Связи проверены");
});

function center(id) {
  const node = positionedNodes.value.find((n) => n.id === id);
  if (!node) return { x: STAGE_W / 2, y: STAGE_H / 2 };
  return { x: node.x + NODE_W / 2, y: node.y + NODE_H / 2 };
}

function edgePath(edge) {
  const a = center(edge.from);
  const b = center(edge.to);
  const mid = (a.x + b.x) / 2;
  return `M${a.x},${a.y} C${mid},${a.y} ${mid},${b.y} ${b.x},${b.y}`;
}
</script>

<style scoped>
.graph-stage {
  @apply relative border border-solid border-border rounded;
  min-height: 280px;
  height: 320px;
  overflow: hidden;
  background-color: hsl(var(--muted) / 0.3);
  background-image: radial-gradient(
    circle,
    rgb(var(--color-purple-100)) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
}
.graph-stage__hint {
  @apply absolute right-3 top-2.5 font-medium uppercase flex items-center gap-1.5;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: rgb(var(--color-gray-400));
  z-index: 1;
}
.graph-stage__hint .dot {
  @apply inline-block rounded-full;
  width: 4px;
  height: 4px;
  background: rgb(var(--color-indigo-500));
}
.graph-stage__hint .dot.is-running {
  animation: ai-graph-blink 1s ease-in-out infinite;
}
.is-idle .graph-stage__hint .dot {
  background: rgb(var(--color-gray-400));
  animation: none;
}

@keyframes ai-graph-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
@keyframes ai-graph-edge-flow {
  0% {
    stroke-dashoffset: 60;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

.node-plate {
  fill: transparent;
  stroke: none;
}

.node-inner {
  height: 100px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  background: hsl(var(--card));
  border: 1px solid rgb(var(--color-purple-100));
  border-radius: 4px;
  box-sizing: border-box;
}
.node-ok .node-inner {
  border-color: rgba(34, 197, 94, 0.35);
}
.node-warn .node-inner {
  border-color: rgba(234, 179, 8, 0.4);
}
.node-bad .node-inner {
  border-color: rgba(239, 68, 68, 0.4);
}
.node-name {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: rgb(var(--color-gray-700));
  line-height: 1.3;
  word-break: break-word;
}
.node-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  line-height: 1.4;
}
.node-bullet {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.node-bullet.is-ok {
  color: rgb(var(--color-green-600));
}
.node-bullet.is-bad {
  color: rgb(var(--color-red-500));
}
.node-bullet.is-warn {
  color: rgb(var(--color-yellow-600));
}

/* Skeleton during running */
.node-name-skeleton {
  display: flex;
  align-items: center;
}
.node-name-skeleton .skeleton-bar--title {
  width: 70%;
  height: 8px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    rgb(var(--color-gray-100)) 0%,
    rgb(var(--color-gray-200)) 50%,
    rgb(var(--color-gray-100)) 100%
  );
  background-size: 200% 100%;
  animation: ai-skeleton-shimmer 1.4s ease-in-out infinite;
}
.node-bullet-skeleton {
  display: flex;
  align-items: center;
}
.node-bullet-skeleton + .node-bullet-skeleton {
  margin-top: 4px;
}
.node-bullet-skeleton .skeleton-bar {
  width: 80%;
  height: 5px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    rgb(var(--color-gray-100)) 0%,
    rgb(var(--color-gray-200)) 50%,
    rgb(var(--color-gray-100)) 100%
  );
  background-size: 200% 100%;
  animation: ai-skeleton-shimmer 1.4s ease-in-out infinite;
}
.node-bullet-skeleton:nth-child(2) .skeleton-bar {
  width: 60%;
}
@keyframes ai-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Reveal text when done */
.node-bullet-reveal {
  opacity: 0;
  transform: translateY(4px);
  animation: ai-bullet-reveal 360ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@keyframes ai-bullet-reveal {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.edge {
  fill: none;
  stroke-width: 1.5;
  stroke-dasharray: 4 6;
  animation: ai-graph-edge-flow 2.4s linear infinite;
}
.edge.is-ok {
  stroke: rgb(99 102 241);
  opacity: 0.55;
}
.edge.is-bad {
  stroke: rgb(220 38 38);
  opacity: 0.75;
}

.edge-particle {
  fill: rgb(99 102 241);
  color: rgb(99 102 241);
  filter: drop-shadow(0 0 4px currentColor);
}
.is-idle .edge-particle {
  display: none;
}

/* In idle/running, edges become neutral */
.is-idle .edge,
.is-running .edge {
  stroke: hsl(var(--border));
  opacity: 0.5;
}
.is-running .edge {
  stroke: rgb(99 102 241);
  opacity: 0.4;
}

.node-pulse-ring {
  fill: none;
  stroke: rgb(99 102 241);
  stroke-width: 1.5;
  transform-origin: center;
  transform-box: fill-box;
  animation: ai-graph-pulse-ring 1.8s ease-out infinite;
}
@keyframes ai-graph-pulse-ring {
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.12);
  }
}

@media (max-width: 768px) {
  .graph-stage {
    min-height: 240px;
    height: 280px;
  }
}
@media (max-width: 480px) {
  .graph-stage {
    min-height: 200px;
    height: 240px;
  }
}
</style>
